/**
 * Validation utilities for authentication forms
 */

import i18n from '@/i18n';

export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Validates email format
 */
export function validateEmail(email: string): ValidationResult {
  if (!email || email.trim() === '') {
    return {
      isValid: false,
      error: i18n.t('common.validation.emailRequired'),
    };
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return {
      isValid: false,
      error: i18n.t('common.validation.emailInvalid'),
    };
  }

  return { isValid: true };
}

/**
 * Validates password strength
 */
export function validatePassword(password: string): ValidationResult {
  if (!password || password.trim() === '') {
    return {
      isValid: false,
      error: i18n.t('common.validation.passwordRequired'),
    };
  }

  if (password.length < 8) {
    return {
      isValid: false,
      error: i18n.t('common.validation.passwordMinLength'),
    };
  }

  if (!/[A-Z]/.test(password)) {
    return {
      isValid: false,
      error: i18n.t('common.validation.passwordUppercase'),
    };
  }

  if (!/[a-z]/.test(password)) {
    return {
      isValid: false,
      error: i18n.t('common.validation.passwordLowercase'),
    };
  }

  if (!/[0-9]/.test(password)) {
    return {
      isValid: false,
      error: i18n.t('common.validation.passwordDigit'),
    };
  }

  return { isValid: true };
}

/**
 * Validates name (first name or last name)
 * `fieldName` doit être un libellé déjà traduit ; par défaut « Ce champ ».
 */
export function validateName(name: string, fieldName?: string): ValidationResult {
  const field = fieldName ?? i18n.t('common.validation.genericFieldLabel');

  if (!name || name.trim() === '') {
    return {
      isValid: false,
      error: i18n.t('common.validation.fieldRequired', { field }),
    };
  }

  if (name.trim().length < 2) {
    return {
      isValid: false,
      error: i18n.t('common.validation.fieldMinLength', { field }),
    };
  }

  const nameRegex = /^[a-zA-ZÀ-ÿ\s'-]+$/;
  if (!nameRegex.test(name)) {
    return {
      isValid: false,
      error: i18n.t('common.validation.fieldLettersOnly', { field }),
    };
  }

  return { isValid: true };
}

/**
 * Validates required field (generic validation)
 * `fieldName` doit être un libellé déjà traduit ; par défaut « Ce champ ».
 */
export function validateRequired(value: string, fieldName?: string): ValidationResult {
  const field = fieldName ?? i18n.t('common.validation.genericFieldLabel');

  if (!value || value.trim() === '') {
    return {
      isValid: false,
      error: i18n.t('common.validation.fieldRequired', { field }),
    };
  }

  return { isValid: true };
}

/**
 * Validates postal code selection
 */
export function validatePostalCode(postalCode: string): ValidationResult {
  return validateRequired(postalCode, i18n.t('common.validation.postalCodeFieldLabel'));
}

/**
 * Validates company selection
 */
export function validateCompany(company: string): ValidationResult {
  return validateRequired(company, i18n.t('common.validation.companyFieldLabel'));
}

/**
 * Validates terms acceptance
 */
export function validateTerms(accepted: boolean): ValidationResult {
  if (!accepted) {
    return {
      isValid: false,
      error: i18n.t('common.validation.termsRequired'),
    };
  }

  return { isValid: true };
}
